import { BookOpen, Check, RefreshCw, Sparkles, Target, Users, Wand2, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DialogFooter } from "../ui/dialog";
import SpeechToTextButton from "../SpeechToTextButton";

const IdeaMode = ({
  step,
  setStep,
  ideaData,
  setIdeaData,
  outline,
  setOutline,
  genres,
  generatingOutline,
  outlineProgress,
  loading,
  onGenerateOutline,
  onCreateFromOutline,
  onBack,
}) => {
  return (
    <>
      {/* Step 1: Enter Idea */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-cyan-900/20 via-slate-800/30 to-cyan-900/15 p-4 rounded-lg border-2 border-cyan-500/30 shadow-sm">
            <h3 className="font-bold text-orange-900 flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5 text-orange-500" />
              Creative Mode — AI Invents Your Story!
            </h3>
            <p className="text-sm text-orange-800 mt-2">
              Share your idea and let AI run wild! I'll invent characters, create plot twists, build worlds, and craft your entire story from scratch.
            </p>
            <div className="mt-3 flex items-center gap-2 text-xs text-orange-700 bg-orange-200/50 px-3 py-2 rounded-md">
              <Zap className="w-4 h-4" />
              <span><strong>Full creative freedom</strong> — AI will surprise you with unexpected twists and original characters!</span>
            </div>
          </div>
          
          <div>
            <Label>What's your story idea? (even just a concept works!) *</Label>
            <div className="relative">
              <Textarea
                placeholder="e.g., 'Superman fights the X-Men' or 'A retired soldier discovers his small town has a dark secret' — AI will invent the characters, twists, and entire plot!"
                value={ideaData.idea}
                onChange={(e) => setIdeaData({ ...ideaData, idea: e.target.value })}
                className="h-24 pr-10"
                data-testid="idea-input"
              />
              <SpeechToTextButton
                onTranscript={(text, append) => {
                  setIdeaData(prev => ({ 
                    ...prev, 
                    idea: append && prev.idea ? prev.idea + ' ' + text : text 
                  }));
                }}
                className="absolute right-2 top-2 h-7 w-7"
                size="icon"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{ideaData.idea.length} characters</p>
          </div>

          <div>
            <Label>Book Title (optional)</Label>
            <Input
              placeholder="Leave blank to let Shweebum suggest a title"
              value={ideaData.title || ""}
              onChange={(e) => setIdeaData({ ...ideaData, title: e.target.value })}
              data-testid="title-input"
            />
            <p className="text-xs text-muted-foreground mt-1">You can change this later</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Location/Setting</Label>
              <Input
                placeholder="e.g., Small town in Georgia, New York City, Medieval France"
                value={ideaData.location || ""}
                onChange={(e) => setIdeaData({ ...ideaData, location: e.target.value })}
              />
            </div>
            <div>
              <Label>Time Period/Year</Label>
              <Input
                placeholder="e.g., Present day, 1942, Year 2150, Victorian era"
                value={ideaData.time_period || ""}
                onChange={(e) => setIdeaData({ ...ideaData, time_period: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label>Genre *</Label>
            <Select value={ideaData.genre} onValueChange={(v) => setIdeaData({ ...ideaData, genre: v })}>
              <SelectTrigger data-testid="genre-select">
                <SelectValue placeholder="Select a genre..." />
              </SelectTrigger>
              <SelectContent>
                {genres.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Plot Points to Include (Optional — AI will invent the rest!)</Label>
            <div className="relative">
              <Textarea
                placeholder={"Give AI some direction (it will create around these):\n- A dramatic betrayal\n- An epic battle scene\n- A shocking twist ending\n\nLeave blank for maximum AI creativity!"}
                value={ideaData.must_have_points || ""}
                onChange={(e) => setIdeaData({ ...ideaData, must_have_points: e.target.value })}
                className="h-20 pr-10"
              />
              <SpeechToTextButton
                onTranscript={(text, append) => {
                  setIdeaData(prev => ({ 
                    ...prev, 
                    must_have_points: append && prev.must_have_points ? prev.must_have_points + '\n- ' + text : '- ' + text 
                  }));
                }}
                className="absolute right-2 top-2 h-7 w-7"
                size="icon"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">These will be woven into your story</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Number of Chapters</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={ideaData.num_chapters}
                onChange={(e) => setIdeaData({ ...ideaData, num_chapters: parseInt(e.target.value) || 10 })}
                className="h-9"
                data-testid="idea-num-chapters"
              />
            </div>
            <div>
              <Label>Target Audience</Label>
              <Select value={ideaData.audience_age} onValueChange={(v) => setIdeaData({ ...ideaData, audience_age: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="children">Children (6-9)</SelectItem>
                  <SelectItem value="middle_grade">Middle Grade (9-12)</SelectItem>
                  <SelectItem value="young_adult">Young Adult (13-18)</SelectItem>
                  <SelectItem value="adult">Adult (18+)</SelectItem>
                  <SelectItem value="mature">Mature Adult</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Content Rating</Label>
              <Select value={ideaData.content_rating} onValueChange={(v) => setIdeaData({ ...ideaData, content_rating: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clean">Clean</SelectItem>
                  <SelectItem value="mild">Mild</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="graphic">Graphic (Gore/Blood)</SelectItem>
                  <SelectItem value="extreme">Extreme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tone</Label>
              <Select value={ideaData.tone} onValueChange={(v) => setIdeaData({ ...ideaData, tone: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light/Humorous</SelectItem>
                  <SelectItem value="dark">Dark/Gritty</SelectItem>
                  <SelectItem value="suspenseful">Suspenseful</SelectItem>
                  <SelectItem value="romantic">Romantic</SelectItem>
                  <SelectItem value="epic">Epic/Grand</SelectItem>
                  <SelectItem value="balanced">Balanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {generatingOutline && (
            <div className="p-4 bg-gradient-to-r from-slate-800/30 to-slate-700/30 rounded-lg border-2 border-cyan-500/30 shadow-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-cyan-200 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  Creating Your Book Outline
                </span>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-card shadow-sm">
                  Step {outlineProgress.part}/{outlineProgress.total}
                </span>
              </div>
              <div className="w-full bg-card rounded-full h-3 overflow-hidden shadow-inner mb-2">
                <div 
                  className={`h-full rounded-full transition-all duration-700 ease-out ${
                    outlineProgress.part === 1 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                    outlineProgress.part === 2 ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                    outlineProgress.part === 3 ? 'bg-gradient-to-r from-cyan-400 to-blue-500' :
                    outlineProgress.part === 4 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                    'bg-blue-300'
                  }`}
                  style={{ width: `${(outlineProgress.part / outlineProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-sm text-cyan-100 text-center font-medium">
                {outlineProgress.message || 'Initializing...'}
              </p>
              <p className="text-xs text-cyan-300 text-center mt-2 animate-pulse">
                ⏳ Large outlines may take 2-3 minutes. Please wait...
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={onBack}>Back</Button>
            <Button 
              onClick={onGenerateOutline}
              className="bg-cyan-700 hover:bg-cyan-800"
              disabled={generatingOutline || ideaData.idea.length < 10}
              data-testid="generate-outline-btn"
            >
              {generatingOutline ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Building Your Story...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Build My Story
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      )}

      {/* Step 2: Review & Customize Outline */}
      {step === 2 && outline && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
            <h3 className="font-semibold text-green-900 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Outline Generated! Review & Customize
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={outline.title} 
                onChange={(e) => setOutline({...outline, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Genre</Label>
              <Select value={outline.genre} onValueChange={(v) => setOutline({...outline, genre: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {genres.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Logline (One-sentence summary)</Label>
            <Textarea 
              value={outline.logline} 
              onChange={(e) => setOutline({...outline, logline: e.target.value})}
              className="h-16"
            />
          </div>

          <div>
            <Label>Setting</Label>
            <Input 
              value={outline.setting} 
              onChange={(e) => setOutline({...outline, setting: e.target.value})}
            />
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Characters ({outline.characters?.length || 0})
            </Label>
            <div className="space-y-2 mt-2 max-h-32 overflow-y-auto">
              {outline.characters?.map((char, idx) => (
                <div key={idx} className="bg-background p-2 rounded text-sm">
                  <strong>{char.name}</strong> ({char.role}) - {char.description}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Plot Points ({outline.plot_points?.length || 0})
            </Label>
            <div className="space-y-1 mt-2 max-h-24 overflow-y-auto">
              {outline.plot_points?.map((point, idx) => (
                <div key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="bg-slate-800/60 text-cyan-300 px-1.5 rounded text-xs">{idx + 1}</span>
                  {point}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Chapter Outline ({outline.chapters?.length || 0} chapters)
            </Label>
            <div className="space-y-1 mt-2 max-h-40 overflow-y-auto bg-background p-2 rounded">
              {outline.chapters?.map((ch, idx) => (
                <div key={idx} className="text-sm border-b border-border pb-1 last:border-0">
                  <strong>Ch {ch.number}: {ch.title}</strong>
                  <p className="text-muted-foreground text-xs">{ch.summary}</p>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setStep(1)}>Back to Idea</Button>
            <Button 
              onClick={onCreateFromOutline}
              className="bg-green-600 hover:bg-green-700"
              disabled={loading}
              data-testid="create-from-outline-btn"
            >
              {loading ? "Creating..." : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create This Book
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      )}
    </>
  );
};

export default IdeaMode;
